import { FC, KeyboardEvent, useRef, useState } from 'react'

interface VerificationInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

const VerificationInput: FC<VerificationInputProps> = ({ value, onChange, disabled = false }) => {
  const [focused, setFocused] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  if (inputRefs.current.length === 0) {
    inputRefs.current = Array(6).fill(null)
  }

  const handleChange = (index: number, inputValue: string) => {
    const newValue = value.split('')
    const digit = inputValue.replace(/[^\d]/g, '').slice(-1)
    newValue[index] = digit

    const combinedValue = newValue.join('')
    onChange(combinedValue)

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && index > 0 && !value[index]) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const digits = pastedData.replace(/[^\d]/g, '').slice(0, 6)
    if (digits) {
      onChange(digits.padEnd(6, ''))
      // Focus last input or first empty input
      const lastIndex = Math.min(digits.length - 1, 5)
      inputRefs.current[lastIndex]?.focus()
    }
  }

  return (
    <div
      className='inline-flex gap-2 p-1 rounded-lg'
      onFocus={() => setFocused(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setFocused(false)
        }
      }}
    >
      {Array(6)
        .fill(0)
        .map((_, index) => (
          <input
            key={index}
            ref={el => (inputRefs.current[index] = el)}
            type='text'
            inputMode='numeric'
            pattern='\d*'
            maxLength={1}
            value={value[index] || ''}
            disabled={disabled}
            className='
            w-10 h-12 text-center text-lg font-semibold
            border border-gray-300 rounded-md
            focus:border-blue-500 focus:outline-none
            disabled:bg-gray-100 disabled:text-gray-400
          '
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            autoComplete='off'
          />
        ))}
    </div>
  )
}

export default VerificationInput
